import React, { useState, useEffect } from 'react'
import { Users, BookOpen, Calendar, Clock, Eye, CheckCircle, XCircle, AlertCircle, Plus, Upload } from 'lucide-react'
import Button from '../../components/common/Button'
import Card from '../../components/common/Card'
import Modal from '../../components/common/Modal'
import Input from '../../components/common/Input'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import Table from '../../components/common/Table'
import { docenteGrupoService } from '../../services/docenteService'
import { evaluacionService } from '../../services/evaluacionService'
import { detalleNotaService } from '../../services/detalleNotaService'
import { periodoService } from '../../services/periodoService'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

const MisGrupos = () => {
  const [grupos, setGrupos] = useState([])
  const [loadingGrupos, setLoadingGrupos] = useState(true)
  const [loadingEvaluaciones, setLoadingEvaluaciones] = useState(false)
  const [loadingEstudiantes, setLoadingEstudiantes] = useState(false)
  const [loadingGuardarNotas, setLoadingGuardarNotas] = useState(false)
  const [showEvaluacionesModal, setShowEvaluacionesModal] = useState(false)
  const [showCrearEvaluacionModal, setShowCrearEvaluacionModal] = useState(false)
  const [showCargarNotasModal, setShowCargarNotasModal] = useState(false)
  const [selectedGrupo, setSelectedGrupo] = useState(null)
  const [selectedGrupoId, setSelectedGrupoId] = useState(null)
  const [evaluaciones, setEvaluaciones] = useState([])
  const [periodos, setPeriodos] = useState([])
  const [notasTemporales, setNotasTemporales] = useState([])
  const [selectedEvaluacion, setSelectedEvaluacion] = useState(null)
  const [selectedPeriodoEvaluaciones, setSelectedPeriodoEvaluaciones] = useState('')
  const [selectedPeriodoNotas, setSelectedPeriodoNotas] = useState('')
  const navigate = useNavigate()
  const { register, handleSubmit, reset, formState: { errors }, watch } = useForm({
    mode: 'onChange'
  })

  useEffect(() => {
    fetchGrupos()
  }, [])

  // Alias para compatibilidad con código antiguo que pueda usar 'loading'
  const loading = loadingGrupos

  const fetchPeriodos = async () => {
    try {
      const response = await periodoService.getPeriodosSimple()
      if (response.success) {
        const periodosList = response.data || []
        setPeriodos(periodosList)
        
        if (periodosList.length === 0) {
          toast('No hay períodos disponibles')
        }
      } else {
        setPeriodos([])
        console.error('Error al cargar períodos:', response.message)
      }
    } catch (error) {
      setPeriodos([])
      console.error('Error al cargar períodos:', error)
    }
  }

  const handleVerEvaluaciones = async (grupo) => {
    setSelectedGrupo(grupo)
    setSelectedGrupoId(grupo.grupo_id)
    await fetchPeriodos()
    setShowEvaluacionesModal(true)
  }

  const handleCargarNotas = async (grupo) => {
    setSelectedGrupo(grupo)
    setSelectedGrupoId(grupo.grupo_id)
    await fetchPeriodos()
    setShowCargarNotasModal(true)
  }

  const fetchEvaluacionesPorGrupo = async (grupoId, periodoId) => {
    try {
      setLoadingEvaluaciones(true)
      const response = await evaluacionService.getEvaluacionesPorGrupoYPeriodo(grupoId, periodoId)
      
      if (response.success) {
        const evaluacionesList = response.data || []
        setEvaluaciones(evaluacionesList)
        
        if (evaluacionesList.length === 0) {
          toast('No hay evaluaciones registradas para este período')
        }
      } else {
        setEvaluaciones([])
        toast.error(response.message || 'Error al cargar evaluaciones')
      }
    } catch (error) {
      setEvaluaciones([])
      console.error('Error al cargar evaluaciones:', error)
      toast.error('Error de conexión al cargar evaluaciones')
    } finally {
      setLoadingEvaluaciones(false)
    }
  }

  const onSubmitEvaluacion = async (data) => {
    if (!data.nombre || !selectedGrupo || !data.periodo_id || !data.porcentaje) {
      toast.error('Completa todos los campos requeridos')
      return
    }

    try {
      setLoadingEvaluaciones(true)
      const response = await evaluacionService.createEvaluacion({
        nombre: data.nombre,
        porcentaje: data.porcentaje,
        grupo_id: selectedGrupo.grupo_id,
        periodo_id: data.periodo_id,
        materia_id: selectedGrupo.modulo?.id,
        descripcion: data.descripcion
      })

      if (response.success) {
        toast.success('Evaluación creada exitosamente')
        setShowCrearEvaluacionModal(false)
        reset()
        // Recargar evaluaciones si la modal está abierta
        if (showEvaluacionesModal) {
          await fetchEvaluacionesPorGrupo(selectedGrupo.grupo_id, data.periodo_id)
        }
      } else {
        toast.error(response.message || 'Error al crear evaluación')
        if (response.errors) {
          Object.values(response.errors).flat().forEach(err => toast.error(err))
        }
      }
    } catch (error) {
      toast.error('Error al crear evaluación')
    } finally {
      setLoadingEvaluaciones(false)
    }
  }

  const fetchGrupos = async () => {
    try {
      setLoadingGrupos(true)
      const response = await docenteGrupoService.getMisGrupos()
      
      if (response.success) {
        setGrupos(response.data || [])
      } else {
        toast.error(response.message || 'Error al cargar grupos')
        setGrupos([])
      }
    } catch (error) {
      toast.error('Error de conexión')
      setGrupos([])
    } finally {
      setLoadingGrupos(false)
    }
  }

  const handleVerDetalle = (grupoId) => {
    navigate(`/docente/grupos/${grupoId}`)
  }

  const handleGuardarNotas = async () => {
    // Validaciones
    if (!selectedPeriodoNotas) {
      toast.error('Selecciona un período')
      return
    }

    if (!selectedEvaluacion) {
      toast.error('Selecciona una evaluación')
      return
    }

    if (notasTemporales.length === 0) {
      toast.error('No hay estudiantes cargados')
      return
    }

    try {
      setLoadingGuardarNotas(true)
      
      // Filtrar solo notas con valor y validar
      const notasAGuardar = notasTemporales
        .filter(n => n.nota !== '' && n.nota !== null && n.nota !== undefined && !isNaN(Number(n.nota)))
        .map(n => ({
          estudiante_id: n.id,
          evaluacion_id: selectedEvaluacion.id,
          nota: Number(n.nota)
        }))

      if (notasAGuardar.length === 0) {
        toast.error('Ingresa al menos una nota antes de guardar')
        return
      }
      const dataNotas = {
              evaluacion_id: selectedEvaluacion.id,
              notas: notasAGuardar
            }
      const response = await detalleNotaService.createNotasMasivo(dataNotas)
      
      if (response.success) {
        toast.success(`${notasAGuardar.length} nota(s) guardada(s) exitosamente`)
        setShowCargarNotasModal(false)
        setSelectedGrupo(null)
        setSelectedGrupoId(null)
        setEvaluaciones([])
        setSelectedEvaluacion(null)
        setNotasTemporales([])
        setSelectedPeriodoNotas('')
      } else {
        toast.error(response.message || 'Error al guardar notas')
        if (response.errors) {
          Object.values(response.errors).flat().forEach(err => toast.error(err))
        }
      }
    } catch (error) {
      console.error('Error:', error)
      toast.error('Error de conexión al guardar notas')
    } finally {
      setLoadingGuardarNotas(false)
    }
  }

  const handleClickEvaluacion = (evaluacion) => {
    setSelectedEvaluacion(evaluacion)
    if (selectedGrupo?.grupo_id) {
      loadEstudiantes(selectedGrupo.grupo_id, evaluacion.id)
    }
  }

  const loadEstudiantes = async (grupoId, evaluacionId) => {
    try {
      setLoadingEstudiantes(true)
      const response = await docenteGrupoService.getEstudiantesGrupo(grupoId, evaluacionId)
      
      if (response.success) {
        const notas = response.data || []
        
        if (notas.length === 0) {
          toast('No hay estudiantes inscritos en este grupo')
          setNotasTemporales([])
          return
        }

        const notasFormato = notas.map(est => ({
          id: est.id || est.estudiante_id,
          nombre: est.nombre,
          apellido: est.apellido,
          nota: est.nota !== undefined && est.nota !== null ? est.nota : '' // Mantener nota vacía si no existe
        }))
        
        setNotasTemporales(notasFormato)
        toast.success(`${notasFormato.length} estudiante(s) cargado(s)`)
      } else {
        toast.error(response.message || 'Error al cargar estudiantes')
        setNotasTemporales([])
      }
    } catch (error) {
      console.error('Error al cargar estudiantes:', error)
      toast.error('Error de conexión al cargar estudiantes')
      setNotasTemporales([])
    } finally {
      setLoadingEstudiantes(false)
    }
  }

  if (loadingGrupos) {
    return <LoadingSpinner />
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center shadow-glow">
          <Users className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold gradient-text">Mis Grupos</h1>
          <p className="text-gray-600 dark:text-gray-400">Grupos asignados para evaluación académica</p>
        </div>
      </div>

      {grupos.length === 0 ? (
        <Card className="gradient" shadow="glow-lg">
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No tienes grupos asignados
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Contacta con el administrador para más información
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {grupos.map((grupo) => (
            <Card key={grupo.grupo_id} className="gradient" shadow="glow-lg">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold gradient-text mb-2">
                      {grupo.programa?.nombre || 'Sin programa'}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <BookOpen className="h-4 w-4 inline mr-1" />
                      {grupo.modulo?.nombre || 'Sin módulo'}
                    </p>
                  </div>
                  {grupo.esta_activo ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3" />
                      Activo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      <XCircle className="h-3 w-3" />
                      Finalizado
                    </span>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>
                      {new Date(grupo.fecha_ini).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })} - {new Date(grupo.fecha_fin).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <Users className="h-4 w-4 mr-2" />
                    <span>{grupo.numero_estudiantes} estudiantes</span>
                  </div>
                  {grupo.horarios && grupo.horarios.length > 0 && (
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Clock className="h-4 w-4 mr-2" />
                      <div className="flex flex-wrap gap-1">
                        {grupo.horarios.map((horario, idx) => (
                          <span key={idx} className="text-xs">
                            {horario.dias} {horario.hora_ini}-{horario.hora_fin}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Eye className="h-4 w-4" />}
                    onClick={() => handleVerEvaluaciones(grupo)}
                  >
                    Evaluaciones
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Upload className="h-4 w-4" />}
                    onClick={() => handleCargarNotas(grupo)}
                  >
                    Notas
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Eye className="h-4 w-4" />}
                    onClick={() => handleVerDetalle(grupo.grupo_id)}
                  >
                    Detalle
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal Ver Evaluaciones */}
      <Modal
        isOpen={showEvaluacionesModal}
        onClose={() => {
          setShowEvaluacionesModal(false)
          setSelectedGrupo(null)
          setSelectedGrupoId(null)
          setEvaluaciones([])
          setSelectedPeriodoEvaluaciones('')
        }}
        title={selectedGrupo ? `Evaluaciones - ${selectedGrupo.programa?.nombre}` : 'Evaluaciones'}
        size="lg"
        closeOnOverlayClick={false}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Seleccionar Período
            </label>
            <select
              value={selectedPeriodoEvaluaciones}
              onChange={(e) => {
                setSelectedPeriodoEvaluaciones(e.target.value)
                if (e.target.value && selectedGrupo) {
                  fetchEvaluacionesPorGrupo(selectedGrupo.grupo_id, e.target.value)
                }
              }}
              className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 pointer-events-auto"
            >
              <option value="">Seleccionar período</option>
              {periodos.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>

          {evaluaciones.length > 0 && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {evaluaciones.map(evaluacion => (
                <div key={evaluacion.id} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{evaluacion.nombre}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Porcentaje: {evaluacion.porcentaje}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loadingEvaluaciones && <p className="text-center text-gray-500">Cargando evaluaciones...</p>}
          {evaluaciones.length === 0 && !loadingEvaluaciones && selectedPeriodoEvaluaciones && (
            <p className="text-center text-gray-500">No hay evaluaciones para este período</p>
          )}

          <div className="flex gap-2 justify-end">
            <Button
              variant="primary"
              onClick={() => setShowCrearEvaluacionModal(true)}
            >
              Nueva Evaluación
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setShowEvaluacionesModal(false)
                setSelectedGrupo(null)
                setSelectedGrupoId(null)
                setEvaluaciones([])
                setSelectedPeriodoEvaluaciones('')
              }}
            >
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Crear Evaluación */}
      <Modal
        isOpen={showCrearEvaluacionModal}
        onClose={() => {
          setShowCrearEvaluacionModal(false)
          reset()
        }}
        title="Nueva Evaluación"
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmitEvaluacion)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Período
            </label>
            <select
              {...register('periodo_id', { required: 'Selecciona un período' })}
              className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Seleccionar período</option>
              {periodos.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
            {errors.periodo_id && <p className="text-red-500 text-sm mt-1">{errors.periodo_id.message}</p>}
          </div>

          <Input
            label="Nombre"
            placeholder="Ej. Evaluación Parcial 1"
            {...register('nombre', { required: 'El nombre es requerido' })}
            error={errors.nombre?.message}
          />

          <Input
            label="Porcentaje"
            type="number"
            min="0"
            max="100"
            placeholder="Ej. 20"
            {...register('porcentaje', { required: 'El porcentaje es requerido' })}
            error={errors.porcentaje?.message}
          />

          <Input
            label="Descripción (Opcional)"
            placeholder="Detalles adicionales"
            {...register('descripcion')}
          />

          <div className="flex gap-2 justify-end border-t pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowCrearEvaluacionModal(false)
                reset()
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loadingEvaluaciones}
            >
              {loadingEvaluaciones ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Cargar Notas */}
      <Modal
        isOpen={showCargarNotasModal}
        onClose={() => {
          setShowCargarNotasModal(false)
          setSelectedGrupo(null)
          setSelectedGrupoId(null)
          setEvaluaciones([])
          setSelectedEvaluacion(null)
          setNotasTemporales([])
          setSelectedPeriodoNotas('')
        }}
        title={selectedGrupo ? `Cargar Notas - ${selectedGrupo.programa?.nombre}` : 'Cargar Notas'}
        size="lg"
        closeOnOverlayClick={false}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Paso 1: Seleccionar Período <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedPeriodoNotas}
              onChange={(e) => {
                const periodoId = e.target.value
                setSelectedPeriodoNotas(periodoId)
                
                if (periodoId && selectedGrupo) {
                  fetchEvaluacionesPorGrupo(selectedGrupo.grupo_id, periodoId)
                  setSelectedEvaluacion(null)
                  setNotasTemporales([])
                } else {
                  setEvaluaciones([])
                  setSelectedEvaluacion(null)
                  setNotasTemporales([])
                }
              }}
              className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 pointer-events-auto"
            >
              <option value="">Seleccionar período</option>
              {periodos.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>

          {!selectedEvaluacion ? (
            <>
              {selectedPeriodoNotas && evaluaciones.length > 0 && (
                <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-400">
                    Paso 2: Selecciona una evaluación
                  </p>
                </div>
              )}
              
              {selectedPeriodoNotas && evaluaciones.length === 0 && !loadingEvaluaciones && (
                <div className="p-3 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700">
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-400">
                    No hay evaluaciones para este período
                  </p>
                </div>
              )}

              {loadingEvaluaciones && selectedPeriodoNotas && (
                <div className="p-3 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Cargando evaluaciones...</p>
                </div>
              )}
              
              {evaluaciones.length > 0 && (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {evaluaciones.map(evaluacion => (
                    <button
                      key={evaluacion.id}
                      onClick={() => handleClickEvaluacion(evaluacion)}
                      className="w-full p-3 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary-500 transition-colors text-left"
                      style={evaluacion.notas.length > 0 ? { borderColor: '#10B981' } : {}}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{evaluacion.nombre}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Porcentaje: {evaluacion.porcentaje}%</p>
                        </div>
                        <span className="text-sm bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-400 px-2 py-1 rounded">
                          {evaluacion.notas.length > 0 ? 'Actualizar' : 'Cargar'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700">
                <p className="text-sm font-medium text-green-800 dark:text-green-400">
                  Paso 3: Ingresa las notas de los estudiantes
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600">
                <p className="font-medium text-gray-900 dark:text-gray-100">{selectedEvaluacion.nombre}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Porcentaje: {selectedEvaluacion.porcentaje}%</p>
                <button
                  onClick={() => setSelectedEvaluacion(null)}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline mt-2"
                >
                  Cambiar evaluación
                </button>
              </div>

              <div className="max-h-96 overflow-y-auto space-y-2">
                {notasTemporales.map((nota) => (
                  <div key={nota.id} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 dark:text-gray-100">{nota.nombre} - {nota.apellido}</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={nota.nota || ''}
                        onChange={(e) => {
                          const updatedNotas = notasTemporales.map(n =>
                            n.id === nota.id ? { ...n, nota: e.target.value } : n
                          )
                          setNotasTemporales(updatedNotas)
                        }}
                        placeholder="0"
                        className="w-20 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {notasTemporales.length === 0 && loadingEstudiantes && (
                <div className="p-3 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Cargando estudiantes del grupo...</p>
                </div>
              )}

              {notasTemporales.length === 0 && !loadingEstudiantes && (
                <div className="p-3 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700">
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-400">
                    No se encontraron estudiantes para esta evaluación
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end border-t pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setShowCargarNotasModal(false)
                setSelectedGrupo(null)
                setSelectedGrupoId(null)
                setEvaluaciones([])
                setSelectedEvaluacion(null)
                setNotasTemporales([])
                setSelectedPeriodoNotas('')
              }}
            >
              Cerrar
            </Button>
            {selectedEvaluacion && notasTemporales.length > 0 && (
              <Button
                variant="primary"
                onClick={handleGuardarNotas}
                disabled={loadingGuardarNotas}
              >
                {loadingGuardarNotas ? 'Guardando...' : 'Guardar Notas'}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default MisGrupos

